/**
 * Site Service — Manages user's audited websites.
 * Lightweight version that works without multi-tier accounts.
 * When accounts/sites tables are built, this upgrades to read from there.
 */

import { supabase } from "@/lib/supabaseClient";

export interface UserSite {
  id: string;
  domain: string;
  displayName: string;
  isActive: boolean;
  lastAuditAt?: string;
  auditCount?: number;
  createdAt: string;
}


export const normalizeDomain = (domain: string): string =>
  domain
    .replace(/^https?:\/\//, "")
    .replace(/^www\./, "")
    .replace(/\/.*$/, "")
    .toLowerCase()
    .trim();

export async function loadUserSites(userId?: string): Promise<UserSite[]> {
  try {
    if (!userId) return [];

    // Try user_sites table first
    const { data: sites, error } = await supabase
      .from("user_sites")
      .select("*")
      .eq("user_id", userId)
      .order("is_active", { ascending: false })
      .order("updated_at", { ascending: false });

    if (!error && sites && sites.length > 0) {
      // Enrich with audit countsYour Sites
      const enriched: UserSite[] = [];

      const { data: audits } = await supabase
        .from("card_audit_results")
        .select("domain, created_at")
        .eq("user_id", userId);

      const auditStats = new Map();

      for (const audit of audits ?? []) {
        const existing = auditStats.get(audit.domain);

        if (!existing) {
          auditStats.set(audit.domain, {
            count: 1,
            lastAudit: audit.created_at,
          });
        } else {
          existing.count++;

          if (new Date(audit.created_at) > new Date(existing.lastAudit)) {
            existing.lastAudit = audit.created_at;
          }
        }
      }

      for (const site of sites) {
        if (!site.domain) continue;
        const auditCount = auditStats.get(site.domain)?.count ?? 0;
        const lastAuditAt = auditStats.get(site.domain)?.lastAudit;
        enriched.push({
          id: site.id,
          domain: site.domain,
          displayName: site.display_name || site.domain,
          isActive: site.is_active,
          auditCount: auditCount,
          lastAuditAt: lastAuditAt,
          createdAt: site.created_at,
        });
      }
      
      return enriched;
    }

    // Fallback: derive sites from existing audit data
    const { data: audits } = await supabase
      .from("card_audit_results")
      .select("domain, created_at")
      .eq("user_id", userId)
      .order("created_at", { ascending: false });

    if (!audits || audits.length === 0) return [];

    const domainMap = new Map<string, { count: number; lastAudit: string }>();
    for (const a of audits) {
      if (!a.domain) continue;
      const existing = domainMap.get(a.domain);
      if (existing) {
        existing.count++;
      } else {
        domainMap.set(a.domain, { count: 1, lastAudit: a.created_at });
      }
    }

    return Array.from(domainMap.entries()).map(([domain, info], i) => ({
      id: `derived-${i}`,
      domain,
      displayName: domain,
      isActive: i === 0,
      lastAuditAt: info.lastAudit,
      auditCount: info.count,
      createdAt: info.lastAudit,
    }));
  } catch (err) {
    console.error("[SiteService] Load error:", err);
    return [];
  }
}

export async function addSite(userId: string | undefined, domain: string, displayName?: string): Promise<UserSite | null> {
  try {
    if (!userId) return null;

    const cleanDomain = domain.replace(/^https?:\/\//, "").replace(/^www\./, "").replace(/\/.*$/, "").toLowerCase();

    const { data, error } = await supabase.rpc("add_user_site_with_plan_limit", {
      p_domain: cleanDomain,
      p_display_name: displayName || cleanDomain,
    });

    if (error) {
      console.error("[SiteService] Add error:", error.message);
      return null;
    }

    const row = Array.isArray(data) ? data[0] : data;
    if (!row) return null;

    return {
      id: row.id,
      domain: row.domain,
      displayName: row.display_name || row.domain,
      isActive: row.is_active,
      auditCount: 0,
      createdAt: row.created_at,
    };
  } catch (err) {
    console.error("[SiteService] Add error:", err);
    return null;
  }
}

export async function setActiveSite(userId: string | undefined, siteId: string): Promise<void> {
  try {
    if (!userId) return;

    // Deactivate all sites
    await supabase
      .from("user_sites")
      .update({ is_active: false })
      .eq("user_id", userId);

    // Activate selected site
    await supabase
      .from("user_sites")
      .update({ is_active: true })
      .eq("id", siteId)
      .eq("user_id", userId);
  } catch (err) {
    console.error("[SiteService] Set active error:", err);
  }
}

export async function deleteSite(userId: string | undefined, siteId: string, domain: string): Promise<boolean> {
  try {
    if (!userId) return false;

    // Delete all audit results for this domain
    await supabase
      .from("card_audit_results")
      .delete()
      .eq("user_id", userId)
      .eq("domain", domain);

    // Delete all chat history for this domain
    await supabase
      .from("chat_history")
      .delete()
      .eq("user_id", userId)
      .eq("domain", domain);

    // Delete the site record
    if (!siteId.startsWith("derived-")) {
      await supabase
        .from("user_sites")
        .delete()
        .eq("id", siteId)
        .eq("user_id", userId);
    }

    console.log(`🗑️ [SiteService] Deleted site ${domain} and all associated data`);
    return true;
  } catch (err) {
    console.error("[SiteService] Delete error:", err);
    return false;
  }
}

export async function getSiteCount(userId?: string): Promise<number> {
  try {
    if (!userId) return 0;

    const { count } = await supabase
      .from("user_sites")
      .select("*", { count: "exact", head: true })
      .eq("user_id", userId);

    if (count !== null) return count;

    // Fallback: count unique domains from audits
    const { data } = await supabase
      .from("card_audit_results")
      .select("domain")
      .eq("user_id", userId);

    return new Set(data?.map((a) => a.domain).filter(Boolean)).size;
  } catch {
    return 0;
  }
}

export async function getMaxSitesForCurrentPlan(userId?: string): Promise<number> {
  if (!userId) return 0;

  const { data, error } = await supabase
    .from("user_subscriptions")
    .select("included_sites")
    .eq("user_id", userId)
    .in("status", ["active", "past_due"])
    .order("updated_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error || !data) return 0;
  return Math.max(0, Number(data.included_sites) || 0);
}

export async function canAddSite(userId?: string): Promise<{ allowed: boolean; current: number; max: number; message?: string }> {
  const [current, max] = await Promise.all([
    getSiteCount(userId),
    getMaxSitesForCurrentPlan(userId),
  ]);

  if (max <= 0) {
    return { allowed: false, current, max, message: "An active remediation plan is required to add a site." };
  }
  if (current >= max) {
    return { allowed: false, current, max, message: `You've reached the maximum of ${max} sites for your plan. Delete a site or upgrade to add more.` };
  }
  return { allowed: true, current, max };
}

/**
 * Auto-register a domain when an audit is run.
 * Called from audit services after successful audits.
 */
export async function autoRegisterDomain(userId: string | undefined, domain: string): Promise<void> {
  if (!userId || !domain || domain === "unknown") return;
  try {
    await addSite(userId, domain);
  } catch {
    // Silent fail — don't block the audit
  }
}

